/**
 * useAppMode
 *
 * Owns the editor display mode: 'simple' (guided, focused modals) vs
 * 'pro' (all panels visible at once — current layout).
 *
 * Persistence: localStorage key `cv_app_mode`.
 *
 * Default: always 'simple' (Simple par défaut — PRODUCT-PLAN décision clé).
 * Once the user has explicitly set a preference, the stored value wins.
 */

import { useCallback, useEffect, useState } from 'react';

export type AppMode = 'simple' | 'pro';

const STORAGE_KEY = 'cv_app_mode';

function readInitialMode(): AppMode {
  if (typeof window === 'undefined') return 'simple';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'simple' || stored === 'pro') return stored;
    return 'simple';
  } catch {
    return 'simple';
  }
}

export interface UseAppModeReturn {
  mode: AppMode;
  setMode: (m: AppMode) => void;
  toggleMode: () => void;
}

export function useAppMode(): UseAppModeReturn {
  const [mode, setModeState] = useState<AppMode>(() => readInitialMode());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const setMode = useCallback((m: AppMode) => {
    setModeState(m);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === 'simple' ? 'pro' : 'simple'));
  }, []);

  return { mode, setMode, toggleMode };
}
